import type React from "react"

import { useCallback, useEffect, useState } from "react"
import { Users, UserPlus, Search, X, Check } from "lucide-react"
import { HRService } from "@/features/hr"
import { AgentAssignmentShimmer, TeamShimmer } from "@/shared/components"

interface Agent {
  id: string
  userId?: string
  firstName: string
  lastName: string
  email: string
  status: "ACTIVE" | "PENDING" | "DEACTIVATED"
  organizationId?: string
  organizationName?: string
}

interface TeamRef {
  id: string
  name: string
}

// An organization (EM/DM/CM/BM/AM) — the client's "teams". Assigning a licensed agent to an
// organization also covers every organization under it. memberCount = licensed agents assigned.
interface Team {
  id: string
  name: string
  organizationLevel?: string
  memberCount: number
}

interface LicensedAgentAssignmentManagerProps {
  onError: (error: string) => void
}

const EMPTY_TEAMS: TeamRef[] = []

export default function LicensedAgentAssignmentManager({ onError }: LicensedAgentAssignmentManagerProps) {
  const [agents, setAgents] = useState<Agent[]>([])
  // agentId -> the teams that agent currently belongs to (many-to-many)
  const [agentTeams, setAgentTeams] = useState<Record<string, TeamRef[]>>({})
  const [teams, setTeams] = useState<Team[]>([])

  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set())
  const [draggedAgent, setDraggedAgent] = useState<Agent | null>(null)
  const [dragOverTeam, setDragOverTeam] = useState<string | null>(null)
  const [busyAgent, setBusyAgent] = useState<string | null>(null)
  const [recentlyUpdatedTeams, setRecentlyUpdatedTeams] = useState<Set<string>>(new Set())

  const [loading, setLoading] = useState(true)
  const [loadingAgents, setLoadingAgents] = useState(false)
  const [loadingTeams, setLoadingTeams] = useState(true)

  const [filters, setFilters] = useState({ search: "", status: "" })
  const [teamSearch, setTeamSearch] = useState("")

  const [showBatchModal, setShowBatchModal] = useState(false)
  const [batchTeamId, setBatchTeamId] = useState("")

  // ---- initial load -------------------------------------------------------

  const buildAgentTeamMap = (list: Array<{ id: string; coverageOrgs?: Array<{ id: string; name: string }> }>) =>
    list.reduce<Record<string, TeamRef[]>>((acc, a) => {
      acc[a.id] = (a.coverageOrgs ?? []).map((t) => ({ id: t.id, name: t.name }))
      return acc
    }, {})

  const LEVEL_LABELS: Record<string, string> = {
    FMO: "FMO Level", EM: "EM Level", DM: "DM Level", CM: "CM Level", BM: "BM Level", AM: "AM Level",
  }

  // Organizations (the client's "teams") with how many licensed agents are assigned to each
  const buildOrgList = (orgs: any[], withTeams: Array<{ coverageOrgs?: Array<{ id: string }> }>): Team[] => {
    const counts = new Map<string, number>()
    withTeams.forEach((a) => (a.coverageOrgs ?? []).forEach((o) => counts.set(o.id, (counts.get(o.id) ?? 0) + 1)))
    return orgs.map((o) => ({
      id: o.id,
      name: o.name,
      organizationLevel: LEVEL_LABELS[o.type] ?? o.type,
      memberCount: counts.get(o.id) ?? 0,
    }))
  }

  const loadInitial = useCallback(async () => {
    try {
      setLoading(true)
      setLoadingTeams(true)

      const [agentsData, withTeams, allTeams] = await Promise.all([
        HRService.getAgentsForAssignment({ isLicensed: true }),
        HRService.getLicensedAgentsWithTeams(),
        HRService.getOrganizationHierarchy(),
      ])

      setAgents(Array.isArray(agentsData?.agents) ? agentsData.agents : [])
      setAgentTeams(buildAgentTeamMap(withTeams as any))
      setTeams(buildOrgList(Array.isArray(allTeams) ? allTeams : [], withTeams as any))
    } catch (error) {
      console.error("Error loading assignment data:", error)
      onError("Failed to load licensed agent assignment data. Please try again.")
    } finally {
      setLoading(false)
      setLoadingTeams(false)
    }
  }, [onError])

  useEffect(() => {
    loadInitial()
  }, [loadInitial])

  // Re-fetch the agent list when search / status filters change (server-side filtering).
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        setLoadingAgents(true)
        const data = await HRService.getAgentsForAssignment({
          search: filters.search || undefined,
          status: filters.status || undefined,
          isLicensed: true,
        })
        if (!cancelled) setAgents(Array.isArray(data?.agents) ? data.agents : [])
      } catch (error) {
        console.error("Error filtering agents:", error)
        if (!cancelled) onError("Failed to filter agents. Please try again.")
      } finally {
        if (!cancelled) setLoadingAgents(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [filters, onError])

  // ---- assignment (additive, many-to-many) -----------------------------

  const flashTeams = (ids: string[]) => {
    setRecentlyUpdatedTeams((prev) => new Set([...Array.from(prev), ...ids]))
    setTimeout(() => {
      setRecentlyUpdatedTeams((prev) => {
        const nextSet = new Set(prev)
        ids.forEach((id) => nextSet.delete(id))
        return nextSet
      })
    }, 2000)
  }

  const bumpTeamCount = (teamId: string, delta: number) => {
    setTeams((prev) =>
      prev.map((t) =>
        t.id === teamId ? { ...t, memberCount: Math.max(0, t.memberCount + delta) } : t,
      ),
    )
  }

  const persistAgentTeams = async (agentId: string, nextTeams: TeamRef[]) =>
    HRService.setAgentOrganizationCoverage(
      agentId,
      nextTeams.map((t) => t.id),
    )

  const addAgentToTeam = async (agent: Agent, team: Team) => {
    const current = agentTeams[agent.id] ?? EMPTY_TEAMS
    if (current.some((t) => t.id === team.id)) {
      onError(`${agent.firstName} ${agent.lastName} is already on ${team.name}`)
      return
    }

    const next = [...current, { id: team.id, name: team.name }]
    setAgentTeams((prev) => ({ ...prev, [agent.id]: next }))
    bumpTeamCount(team.id, +1)
    flashTeams([team.id])
    setBusyAgent(agent.id)

    try {
      await persistAgentTeams(agent.id, next)
    } catch (error: any) {
      setAgentTeams((prev) => ({ ...prev, [agent.id]: current }))
      bumpTeamCount(team.id, -1)
      onError(resolveError(error, `Failed to assign ${agent.firstName} ${agent.lastName} to ${team.name}`))
    } finally {
      setBusyAgent(null)
    }
  }

  const removeAgentFromTeam = async (agentId: string, team: TeamRef) => {
    const current = agentTeams[agentId] ?? EMPTY_TEAMS
    const next = current.filter((t) => t.id !== team.id)
    setAgentTeams((prev) => ({ ...prev, [agentId]: next }))
    bumpTeamCount(team.id, -1)
    setBusyAgent(agentId)

    try {
      await persistAgentTeams(agentId, next)
    } catch (error: any) {
      setAgentTeams((prev) => ({ ...prev, [agentId]: current }))
      bumpTeamCount(team.id, +1)
      onError(resolveError(error, `Failed to remove agent from ${team.name}`))
    } finally {
      setBusyAgent(null)
    }
  }

  const handleBatchAssign = async () => {
    const team = teams.find((t) => t.id === batchTeamId)
    if (selectedAgents.size === 0 || !team) {
      onError("Select licensed agents and a team first")
      return
    }

    const ids = Array.from(selectedAgents)
    const toAdd = ids.filter((id) => !(agentTeams[id] ?? EMPTY_TEAMS).some((t) => t.id === team.id))

    try {
      await Promise.all(
        toAdd.map((id) => {
          const current = agentTeams[id] ?? EMPTY_TEAMS
          return persistAgentTeams(id, [...current, { id: team.id, name: team.name }])
        }),
      )

      setAgentTeams((prev) => {
        const next = { ...prev }
        toAdd.forEach((id) => {
          const current = next[id] ?? EMPTY_TEAMS
          next[id] = [...current, { id: team.id, name: team.name }]
        })
        return next
      })
      bumpTeamCount(team.id, toAdd.length)
      flashTeams([team.id])

      setSelectedAgents(new Set())
      setShowBatchModal(false)
      setBatchTeamId("")
    } catch (error: any) {
      onError(resolveError(error, "Failed to batch assign licensed agents"))
    }
  }

  // ---- drag and drop --------------------------------------------------

  const handleDragStart = (agent: Agent) => setDraggedAgent(agent)

  const handleDragOver = (e: React.DragEvent, teamId: string) => {
    e.preventDefault()
    setDragOverTeam(teamId)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOverTeam(null)
  }

  const handleDrop = async (e: React.DragEvent, team: Team) => {
    e.preventDefault()
    setDragOverTeam(null)
    if (!draggedAgent) return
    const agent = draggedAgent
    setDraggedAgent(null)
    await addAgentToTeam(agent, team)
  }

  // ---- selection helpers --------------------------------------------

  const filteredAgents = agents
  const visibleTeams = teamSearch.trim()
    ? teams.filter((t) => t.name.toLowerCase().includes(teamSearch.trim().toLowerCase()))
    : teams

  const toggleAgent = (agentId: string) => {
    setSelectedAgents((prev) => {
      const next = new Set(prev)
      if (next.has(agentId)) next.delete(agentId)
      else next.add(agentId)
      return next
    })
  }

  const toggleSelectAll = () => {
    setSelectedAgents((prev) =>
      prev.size === filteredAgents.length ? new Set() : new Set(filteredAgents.map((a) => a.id)),
    )
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-100 text-green-800"
      case "PENDING":
        return "bg-yellow-100 text-yellow-800"
      case "DEACTIVATED":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  // ---- render -------------------------------------------------------

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-accent-900">Licensed Agent Assignment</h2>
          <p className="text-sm text-accent-600 mt-1">
            Assign licensed agents to one or more organizations (teams). A licensed agent can work for multiple organizations.
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowBatchModal(true)}
            disabled={selectedAgents.size === 0}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserPlus className="w-4 h-4" />
            <span>Batch Assign ({selectedAgents.size})</span>
          </button>
        </div>
      </div>

      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search licensed agents..."
              className="input-field pl-10"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>
          <div>
            <select
              className="input-field"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="ACTIVE">Active</option>
              <option value="DEACTIVATED">Deactivated</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Licensed agents */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-accent-900">Licensed Agents ({filteredAgents.length})</h3>
            <button onClick={toggleSelectAll} className="text-sm text-primary-600 hover:text-primary-800">
              {selectedAgents.size === filteredAgents.length && filteredAgents.length > 0 ? "Deselect All" : "Select All"}
            </button>
          </div>

          <div className="space-y-2 max-h-[32rem] overflow-y-auto">
            {loading || loadingAgents ? (
              <AgentAssignmentShimmer rows={5} />
            ) : filteredAgents.length === 0 ? (
              <p className="text-sm text-accent-500 py-6 text-center">No licensed agents found.</p>
            ) : (
              filteredAgents.map((agent) => {
                const assigned = agentTeams[agent.id] ?? EMPTY_TEAMS
                return (
                  <div
                    key={agent.id}
                    draggable={busyAgent !== agent.id}
                    onDragStart={() => handleDragStart(agent)}
                    className={`p-3 border rounded-lg transition-all duration-200 ${
                      busyAgent === agent.id ? "opacity-50 cursor-not-allowed" : "cursor-move hover:bg-accent-50 hover:shadow-sm"
                    } ${selectedAgents.has(agent.id) ? "border-primary-300 bg-primary-50" : "border-accent-200"} ${
                      draggedAgent?.id === agent.id ? "opacity-50 scale-95" : ""
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        checked={selectedAgents.has(agent.id)}
                        onChange={() => toggleAgent(agent.id)}
                        disabled={busyAgent === agent.id}
                        className="mt-1 rounded border-accent-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-accent-900 truncate">
                            {agent.firstName} {agent.lastName}
                            {busyAgent === agent.id && <span className="ml-2 text-xs text-blue-600">Saving...</span>}
                          </p>
                          <span className={`px-2 py-1 text-xs rounded-full flex-shrink-0 ${getStatusColor(agent.status)}`}>
                            {agent.status}
                          </span>
                        </div>
                        <p className="text-xs text-accent-500 truncate">{agent.email}</p>

                        <div className="mt-2 flex flex-wrap gap-1">
                          {assigned.length === 0 ? (
                            <span className="text-xs text-accent-400 italic">No organization assignments</span>
                          ) : (
                            assigned.map((team) => (
                              <span
                                key={team.id}
                                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-primary-100 text-primary-800"
                              >
                                {team.name}
                                <button
                                  type="button"
                                  onClick={() => removeAgentFromTeam(agent.id, team)}
                                  disabled={busyAgent === agent.id}
                                  className="hover:text-primary-950 disabled:opacity-50"
                                  aria-label={`Remove ${agent.firstName} from ${team.name}`}
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* Teams */}
        <div className="card">
          <div className="flex items-center justify-between mb-3 gap-3">
            <h3 className="text-lg font-semibold text-accent-900">Organizations ({visibleTeams.length})</h3>
            <div className="relative w-44">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Filter organizations..."
                className="input-field pl-9 py-1.5 text-sm"
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
              />
            </div>
          </div>

          {loading || loadingTeams ? (
            <div className="max-h-[32rem] overflow-y-auto">
              <TeamShimmer rows={3} />
            </div>
          ) : teams.length === 0 ? (
            <div className="text-center py-8 text-accent-500">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No organizations found.</p>
            </div>
          ) : visibleTeams.length === 0 ? (
            <p className="text-sm text-accent-500 py-6 text-center">No organizations match "{teamSearch}".</p>
          ) : (
            <div className="space-y-3 max-h-[32rem] overflow-y-auto">
              {visibleTeams.map((team) => {
                const draggedAssigned =
                  !!draggedAgent && (agentTeams[draggedAgent.id] ?? EMPTY_TEAMS).some((t) => t.id === team.id)
                return (
                  <div
                    key={team.id}
                    onDragOver={(e) => handleDragOver(e, team.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, team)}
                    className={`p-4 border-2 border-dashed rounded-lg transition-all duration-200 ${
                      dragOverTeam === team.id
                        ? "border-primary-500 bg-primary-100 scale-[1.02] shadow-lg"
                        : "border-accent-300 hover:border-primary-400 hover:bg-primary-50"
                    } ${draggedAssigned ? "border-amber-300 bg-amber-50" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-accent-900">{team.name}</h4>
                        {team.organizationLevel && (
                          <p className="text-xs text-blue-600 font-medium">{team.organizationLevel}</p>
                        )}
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-accent-500">
                            <span
                              className={`font-semibold transition-all duration-500 ${
                                recentlyUpdatedTeams.has(team.id)
                                  ? "text-green-600 scale-110"
                                  : team.memberCount > 0
                                    ? "text-green-600"
                                    : "text-accent-500"
                              }`}
                            >
                              {team.memberCount}
                            </span>
                            {" "}licensed agent{team.memberCount === 1 ? "" : "s"} assigned
                          </p>
                          {dragOverTeam === team.id && draggedAgent && !draggedAssigned && (
                            <span className="text-xs text-blue-600 font-medium animate-pulse">
                              Add {draggedAgent.firstName} {draggedAgent.lastName}
                            </span>
                          )}
                          {draggedAssigned && dragOverTeam === team.id && (
                            <span className="text-xs text-amber-600 font-medium">Already assigned</span>
                          )}
                        </div>
                      </div>
                      <div
                        className={`w-12 h-12 rounded-lg flex items-center justify-center transition-colors ${
                          dragOverTeam === team.id ? "bg-primary-200" : "bg-primary-100"
                        }`}
                      >
                        <Users className="w-6 h-6 text-primary-600" />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Batch assign modal */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-accent-900">
                Batch Assign {selectedAgents.size} Licensed Agent{selectedAgents.size === 1 ? "" : "s"}
              </h3>
              <button onClick={() => setShowBatchModal(false)} className="text-accent-400 hover:text-accent-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-accent-600">
                Selected agents are <strong>added</strong> to the chosen organization; their existing assignments are kept.
                An organization also covers every organization under it.
              </p>

              <div>
                <label className="block text-sm font-medium text-accent-700 mb-1">Organization *</label>
                {loadingTeams ? (
                  <div className="h-10 bg-accent-100 rounded w-full animate-pulse" />
                ) : (
                  <select
                    className="input-field"
                    value={batchTeamId}
                    onChange={(e) => setBatchTeamId(e.target.value)}
                  >
                    <option value="">Select Organization</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                        {team.organizationLevel ? ` (${team.organizationLevel})` : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button onClick={() => setShowBatchModal(false)} className="btn-outline">
                Cancel
              </button>
              <button
                onClick={handleBatchAssign}
                className="btn-primary flex items-center space-x-2"
                disabled={!batchTeamId}
              >
                <Check className="w-4 h-4" />
                <span>Add to Organization</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function resolveError(error: any, fallback: string): string {
  const message =
    error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback
  if (typeof message === "string" && message.toLowerCase().includes("not found or inactive")) {
    return "One or more teams are no longer available. Please refresh and try again."
  }
  return message || fallback
}
